/** @format */

import {CardElement, useElements, useStripe} from '@stripe/react-stripe-js'
import React, {useContext, useEffect, useState} from 'react'

import {Alert} from '@src/components/tailwind/Alert'
import LoaderButton from '@src/components/tailwind/LoaderButton'
import Spinner from '@src/components/tailwind/Spinner'

import ajax from '@src/ajax'
import {AccountContext} from '@src/contexts/AccountContext'
import {LoadingContext} from '@src/contexts/LoadingContext'
import * as toast from '@src/toast'
import {featureTrialMinimumProduct, isTrialPlan} from '@src/utils'

import {CheckoutContext} from './CheckoutContext'

const amplitude = window.amplitude

export const PaymentForm = props => {
  const stripe = useStripe()
  const elements = useElements()
  const {closeModal, checkoutData, updateCheckoutData} =
    useContext(CheckoutContext)
  const [coupon, setCoupon] = useState('')
  const [typingCoupon, setTypingCoupon] = useState(false)
  const [estimate, setEstimate] = useState('...')

  const {account, profile, sub} = useContext(AccountContext)
  const {ready} = useContext(LoadingContext)

  useEffect(() => {
    updateEstimate()
  }, [checkoutData.stripePlan])

  useEffect(() => {
    if (checkoutData.zip.length === 5 && checkoutData.country === 'US') {
      updateEstimate()
    }
  }, [checkoutData.zip])

  useEffect(() => {
    if (!typingCoupon) {
      const timeout = setTimeout(() => {
        updateEstimate()
        setTypingCoupon(false)
      }, 750)
      setTypingCoupon(true)
      return () => {
        clearTimeout(timeout)
        setTypingCoupon(false)
      }
    }
  }, [coupon])

  let {userHasSubAlreadySoDontAsk} = props
  let {monthly: m, yearly: y} = props.product
  const isCurrentPlan = period => account?.plan === props.product[period]?.stripe_plan

  const subscribe = async () => {
    await ajax({
      method: 'POST',
      endpoint: '/api-int/subscribe',
      onSuccess: async data => {
        amplitude.logEvent('Subscribed')
        gtag_report_conversion()
        closeModal()
        setTimeout(() => {
          window.location.href = '/forms'
        }, 500)
      },
      onError: () => {
        ready()
      },
      errorMsg: 'Failed to subscribe',
      successMsg: 'Successfully subscribed!',
      encourageRetry: false,
    })
  }

  const completeCheckout = async e => {
    e.persist()
    e.preventDefault()
    const cardElement = elements.getElement(CardElement)

    let payload = {
      plan: checkoutData.stripePlan,
      coupon,
    }

    if (!userHasSubAlreadySoDontAsk) {
      let {paymentMethod, error} = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: {
          name: checkoutData.name,
          email: checkoutData.email || profile.email,
          address: {
            line1: checkoutData.address,
            city: checkoutData.city,
            state: checkoutData.state,
            postal_code: checkoutData.zip,
            country: checkoutData.country,
          },
        },
      })
      if (error) {
        toast.warning(error.message)
        ready()
        return
      }

      payload.payment_method_id = paymentMethod.id
    }

    if (checkoutData.email && checkoutData.password) {
      payload.email = checkoutData.email
      payload.password = checkoutData.password
    }

    amplitude.logEvent('Clicked Checkout Next', {step: 'payment'})

    await ajax({
      method: 'POST',
      endpoint: '/api-int/stripe-subscription',
      payload,
      onSuccess: async data => {
        let payment_method = cardElement
          ? {card: cardElement}
          : data.default_payment_method

        if (data.payment_intent && data.payment_intent.status !== 'succeeded') {
          await stripe
            .confirmCardPayment(data.payment_intent.client_secret, {
              payment_method: payment_method,
              setup_future_usage: 'off_session',
            })
            .then(async result => {
              if (result.error) {
                toast.error(result.error.message)
                ready()
                // TODO: HOW CAN I RETURN FROM ONSUCCESS HERE TO AVOID DUPLICATED CODE BELLOW?
              } else {
                await subscribe()
              }
            })
        } else {
          await subscribe()
        }
      },
      onError: (err, resp) => {
        let errorMsg
        if (resp.status === 403 && !resp.headers['x-fs-worker']) {
          // check for blocked payment
          errorMsg = 'Could not complete payment. Please reach out to support.'
        } else {
          errorMsg = `Failed to purchase: ${
            err?.errors?.join(' ') || err?.error || err || 'Unknown error'
          }`
        }
        toast.error(errorMsg)
        ready()
      },
      encourageRetry: false,
      errorMsg: null,
    })
  }

  const updateEstimate = async () => {
    if (!checkoutData.stripePlan) return
    setEstimate(<Spinner type="circle" className="h-4 w-4 pt-1 text-black" />)
    await ajax({
      method: 'GET',
      endpoint: `/api-int/estimate?plan=${checkoutData.stripePlan}&coupon=${coupon}&postalCode=${checkoutData.zip}&country=${checkoutData.country}`,
      onSuccess: async data => {
        setEstimate(data.amount)
      },
      onError: async data => {
        setEstimate('error')
      },
    })
  }

  return (
    <form onSubmit={completeCheckout} className={'w-full'}>
      <div className={'mb-2'}>
        {isTrialPlan(account.plan) &&
        !formspree.products
          .find(product => product.id === account.product)
          .available_products.includes(props.product.id) ? (
          <Alert type="warning" className={'mb-4'}>
            <div className="text-sm text-yellow-700">
              {props.product.name} is missing features on your current trial. To
              continue with your existing features, please select{' '}
              {featureTrialMinimumProduct(account.product).name} or higher.
            </div>
          </Alert>
        ) : null}
        <label className={'mb-2 block' + (isCurrentPlan('yearly') ? ' opacity-50' : ' cursor-pointer')}>
          <input
            type="radio"
            name="stripePlan"
            checked={checkoutData.period === 'yearly'}
            onChange={e => {
              updateCheckoutData({
                period: 'yearly',
                stripePlan: e.target.value,
              })
            }}
            value={y.stripe_plan}
            required
            disabled={isCurrentPlan('yearly')}
            className={'inline'}
          />{' '}
          <span className="pl-2 font-bold">Yearly Billing</span> $
          {(y.price / 12).toFixed(0)}
          /mo ($
          {y.price} total, ${(m.price * 12 - y.price).toFixed(0)} discount){' '}
          {isCurrentPlan('yearly') && <span className="text-gray-500">(current plan)</span>}
        </label>
        <label className={'mb-2 block' + (isCurrentPlan('monthly') ? ' opacity-50' : ' cursor-pointer')}>
          <input
            type="radio"
            name="stripePlan"
            checked={checkoutData.period === 'monthly'}
            onChange={e => {
              updateCheckoutData({
                period: 'monthly',
                stripePlan: e.target.value,
              })
            }}
            value={m.stripe_plan}
            required
            disabled={isCurrentPlan('monthly')}
            className={'inline'}
          />{' '}
          <span className="pl-2 font-bold">Monthly Billing</span> ${m.price}
          /mo{' '}
          {isCurrentPlan('monthly') && <span className="text-gray-500">(current plan)</span>}
        </label>
      </div>
      {!sub && (
        <div>
          <label>
            Coupon code:{' '}
            <input
              className={''}
              type={'text'}
              name="coupon"
              onChange={e => setCoupon(e.target.value)}
              value={coupon}
            />
          </label>
        </div>
      )}
      <div className="mt-6">
        <p className="inline-flex">
          Total amount to be charged today:&nbsp;<b>{estimate}</b>
        </p>
      </div>
      {userHasSubAlreadySoDontAsk ? (
        <div className="text-sm text-gray-500">
          Your card on file will be charged.
        </div>
      ) : (
        <div className="mt-4">
          <CardElement
            options={{
              style: {base: {fontSize: '1.35rem'}},
            }}
            onChange={e => updateCheckoutData('zip', e.value.postalCode)}
            className="input"
            hidePostalCode
          />
          <p className="mt-1 text-right">Payment handled by Stripe</p>
        </div>
      )}
      <LoaderButton className="mt-4 w-full" disabled={isCurrentPlan(checkoutData.period)}>Purchase</LoaderButton>
    </form>
  )
}
